import { useState, useEffect, useMemo } from 'react'
import { Plus, ChevronRight, ArrowLeft, Play, Clock, Check, X, Trophy, RotateCcw, TrendingDown, Target, Star, DollarSign, Pencil, RefreshCw, Trash2, Users, GripVertical, AlertTriangle } from 'lucide-react'
import { useMatch, useUpdateMatch, useUpdateMatchPlayer, useDeleteMatch, useReorderSessions, useActivateSession, useDeactivateSession } from '../hooks/useMatches'
import { useResumeGame } from '../hooks/useGames'
import { useBatchRefreshHandicaps } from '../hooks/usePlayers'
import { useSideBets, useCreateSideBet, useDeleteSideBet } from '../hooks/useSideBets'
import { useAuth } from '../contexts/AuthContext'
import { getInitials } from '../utils/colors'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { formatHandicapIndex, splitHandicapForInput, combineHandicapInput } from '../utils/handicaps'
import PlusHandicapToggle from './shared/PlusHandicapToggle'
import { buildSideBetHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { computeMatchLowNetMoney, computeMatchLowGrossMoney, computeMatchStablefordMoney, computeMatchSkinsMoney } from '../utils/sideBetEngine'
import { stablefordPoints } from '../utils/scoringEngine'
import { buildMatchSnapshot } from '../utils/resultSnapshot'
import Spinner from './Spinner'
import RoundSetupWizard from './RoundSetupWizard'
import SessionSetupWizard from './SessionSetupWizard'
import GameTypeBadge from './GameTypeBadge'
import SideGameConfig from './SideGameConfig'
import { useRoundResults } from '../hooks/useGameScoring'
import { getMatchTypeLabels } from '../utils/matchTypeLabels'
import { onlineManager } from '@tanstack/react-query'
import logger from '../utils/logger'

export default function MatchDashboard({ matchId, onBack, onGoHome, onDeletedMatch, onViewRound, onStartRound, onEndRound, onManageRoster, onAddPlayers, onViewProfile, onSetupTeams, onSetupBuyIn, onNavigate, onViewSession, openSessionWizard, onSessionWizardOpened, onSessionWizardClosed, onSessionWizardCompleted, onWizardToggle }) {
  const { user } = useAuth()
  const { data: match, isLoading, error, refetch, isFetching } = useMatch(matchId)
  const updateMatch = useUpdateMatch()
  const deleteMatch = useDeleteMatch()
  const resumeGame = useResumeGame()
  const updateMatchPlayer = useUpdateMatchPlayer()
  const deactivateSessionMutation = useDeactivateSession()
  const batchRefresh = useBatchRefreshHandicaps()
  const { data: sideBets } = useSideBets(matchId)
  const createSideBet = useCreateSideBet()
  const deleteSideBet = useDeleteSideBet()
  const [editingHandicap, setEditingHandicap] = useState(null)
  const [showRoundWizard, setShowRoundWizard] = useState(() => {
    try {
      const raw = localStorage.getItem(`press_wizard_${matchId}`)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed?.phase === 'round-setup') return true
      }
    } catch { /* ignore */ }
    return false
  })
  const [showSessionWizard, setShowSessionWizard] = useState(!!openSessionWizard)
  const [sessionWizardRestored, setSessionWizardRestored] = useState(!!openSessionWizard)
  const [editingSession, setEditingSession] = useState(null)
  const [showResumeConfirm, setShowResumeConfirm] = useState(null)
  const [showFinishConfirm, setShowFinishConfirm] = useState(false)
  const [hasIncompleteGames, setHasIncompleteGames] = useState(false)
  const [showReopenConfirm, setShowReopenConfirm] = useState(false)
  // Notify parent when any wizard modal is open (so BottomTabs can hide)
  useEffect(() => {
    onWizardToggle?.(showRoundWizard || showSessionWizard || !!editingSession)
  }, [showRoundWizard, showSessionWizard, editingSession, onWizardToggle])

  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState('')
  const [setupName, setSetupName] = useState(() => {
    try {
      const saved = localStorage.getItem(`press_setup_name_${matchId}`)
      if (saved != null) return saved
    } catch { /* ignore */ }
    return null
  }) // null = not yet loaded from match data
  const [setupNameLoaded, setSetupNameLoaded] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSideGameConfig, setShowSideGameConfig] = useState(false)
  const [loadingTooLong, setLoadingTooLong] = useState(false)
  const [isReorderingSessions, setIsReorderingSessions] = useState(false)
  const [showTeamEditActions, setShowTeamEditActions] = useState(false)
  const [draggedSessionId, setDraggedSessionId] = useState(null)
  const reorderSessionsMutation = useReorderSessions()
  const activateSessionMutation = useActivateSession()
  const [showActivateConfirm, setShowActivateConfirm] = useState(null)


  // Listen for Android back gesture triggering discard (setup matches only)
  useEffect(() => {
    const handler = () => setShowDeleteConfirm(true)
    window.addEventListener('press:back-discard', handler)
    return () => window.removeEventListener('press:back-discard', handler)
  }, [])

  // Show retry hint after 8 seconds of loading
  useEffect(() => {
    if (!isLoading && !error) {
      setLoadingTooLong(false)
      return
    }
    const timer = setTimeout(() => setLoadingTooLong(true), 8000)
    return () => clearTimeout(timer)
  }, [isLoading, error])

  // Populate setupName from match data on first load (localStorage takes priority if set)
  useEffect(() => {
    if (!setupNameLoaded && match?.status === 'setup') {
      if (setupName == null) {
        const hasDefaultName = /^(Golf Trip|Invitational) - /.test(match.match_name)
        setSetupName(hasDefaultName ? '' : match.match_name)
      }
      setSetupNameLoaded(true)
    }
  }, [match, setupNameLoaded])

  // Persist setupName to localStorage on every change
  useEffect(() => {
    if (setupName == null) return
    try { localStorage.setItem(`press_setup_name_${matchId}`, setupName) } catch { /* ignore */ }
  }, [setupName, matchId])

  // Auto-open session wizard when navigating back from team setup
  useEffect(() => {
    if (openSessionWizard && match) {
      setShowSessionWizard(true)
      onSessionWizardOpened?.()
    }
  }, [openSessionWizard, match])

  if (isLoading) {
    // If restoring into session wizard, render it over the loading state
    if (showSessionWizard) {
      return (
        <SessionSetupWizard
          matchId={matchId}
          skipAnimation
          onClose={() => { setShowSessionWizard(false); onSessionWizardClosed?.() }}
          onSessionCreated={() => {
            onSessionWizardCompleted?.()
            setShowSessionWizard(false)
          }}
        />
      )
    }
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spinner message="Loading..." />
        {loadingTooLong && (
          <div style={{ marginTop: '1.5rem' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
              Taking longer than usual...
            </p>
            <button
              className="btn-primary"
              onClick={() => refetch()}
              disabled={isFetching}
              style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
            >
              <RefreshCw size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '0.375rem' }} />
              Retry
            </button>
          </div>
        )}
      </div>
    )
  }

  if (!match) {
    if (error) logger.capture(error, { source: 'MatchDashboard', matchId })
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
          {error ? `Error: ${error.message}` : 'Event not found'}
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button
            className="btn-primary"
            onClick={() => refetch()}
            disabled={isFetching}
            style={{ padding: '0.75rem 1.5rem' }}
          >
            <RefreshCw size={16} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '0.375rem' }} />
            {isFetching ? 'Retrying...' : 'Retry'}
          </button>
          <button
            onClick={onBack}
            style={{ padding: '0.75rem 1.5rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}
          >
            Back
          </button>
        </div>
      </div>
    )
  }

  const isOwner = match.owner_id === user?.id
  const matchPlayers = match.match_players || []
  const rounds = match.games || []
  const labels = getMatchTypeLabels(match?.match_type)
  const isTeamMatchSetup = isOwner && match.match_type === 'team_match' && match.status === 'active' && !((match.match_teams || []).length >= 2 && (match.match_teams || []).some(t => (t.match_team_players || []).length > 0))


  const handleHandicapSave = (matchPlayerId) => {
    if (!editingHandicap) return
    const val = combineHandicapInput({ isPlus: editingHandicap.isPlus, magnitude: editingHandicap.magnitude })
    if (val == null) {
      setEditingHandicap(null)
      return
    }

    updateMatchPlayer.mutate(
      { id: matchPlayerId, match_id: matchId, handicap_index: val },
      { onSuccess: () => setEditingHandicap(null) }
    )
  }

  const handleToggleScorer = (mp) => {
    updateMatchPlayer.mutate({
      id: mp.id,
      match_id: matchId,
      is_scorer: !mp.is_scorer,
    })
  }

  const handleStartEditName = () => {
    setNameValue(match.match_name)
    setEditingName(true)
  }

  const handleSaveName = () => {
    const trimmed = nameValue.trim()
    if (!trimmed || trimmed === match.match_name) {
      setEditingName(false)
      return
    }
    updateMatch.mutate(
      { id: matchId, match_name: trimmed },
      { onSuccess: () => setEditingName(false) }
    )
  }

  const flushSetupName = () => {
    const trimmed = (setupName ?? '').trim()
    if (trimmed && trimmed !== match.match_name) {
      updateMatch.mutate({ id: matchId, match_name: trimmed })
    }
  }

  const handleActivateMatch = () => {
    updateMatch.mutate({ id: matchId, status: 'active' }, {
      onSuccess: () => {
        try { localStorage.removeItem(`press_setup_name_${matchId}`) } catch { /* ignore */ }
      },
    })
  }

  const handleCompleteMatch = () => {
    // Compute match-level side bet snapshots
    const matchLowNetBet = sideBets?.find(b => b.bet_type === 'low_net' && !b.game_id && b.scope === 'match')
    const matchLowGrossBet = sideBets?.find(b => b.bet_type === 'low_gross' && !b.game_id && b.scope === 'match')
    const matchStablefordBet = sideBets?.find(b => b.bet_type === 'stableford' && !b.game_id && b.scope === 'match')
    const matchSkinsBet = sideBets?.find(b => (b.bet_type === 'skins' || b.bet_type === 'skins_carry') && !b.game_id && b.scope === 'match')
    const completedGames = rounds.filter(g => g.status === 'completed')
    const hasAnyMatchBet = matchLowNetBet || matchLowGrossBet || matchStablefordBet || matchSkinsBet

    let matchSnapshot = null
    if (hasAnyMatchBet && completedGames.length > 0) {
      // Build roundsData once — shared by all computations
      const roundsData = completedGames.map(game => {
        const course = getGameCourse(game)
        const tee = getGameTee(game)
        const gamePlayers = game.game_players || []
        const playerIds = gamePlayers.map(gp => gp.player_id)

        const handicapBreakdown = buildSideBetHandicapBreakdown({
          matchPlayers, gamePlayers, course, tee, mixedTees: getMixedTees(game),
        })
        return {
          scores: game.scores || [],
          handicapBreakdown,
          numHoles: game.num_holes || 18,
          playerIds,
          holePars: course?.par || null,
        }
      }).filter(r => r.handicapBreakdown)

      if (roundsData.length > 0) {
        let matchLowNetResult = null
        let matchLowGrossResult = null
        let matchStablefordResult = null
        let matchSkinsResult = null

        if (matchLowNetBet) {
          const allPlayerIds = matchPlayers
            .filter(mp => new Date(mp.created_at) <= new Date(matchLowNetBet.created_at))
            .map(mp => mp.player_id)
          matchLowNetResult = computeMatchLowNetMoney(roundsData, matchLowNetBet, allPlayerIds)
        }
        if (matchLowGrossBet) {
          const allPlayerIds = matchPlayers
            .filter(mp => new Date(mp.created_at) <= new Date(matchLowGrossBet.created_at))
            .map(mp => mp.player_id)
          matchLowGrossResult = computeMatchLowGrossMoney(roundsData, matchLowGrossBet, allPlayerIds)
        }
        if (matchStablefordBet) {
          const allPlayerIds = matchPlayers
            .filter(mp => new Date(mp.created_at) <= new Date(matchStablefordBet.created_at))
            .map(mp => mp.player_id)
          matchStablefordResult = computeMatchStablefordMoney(roundsData, matchStablefordBet, allPlayerIds)
        }
        if (matchSkinsBet) {
          const allPlayerIds = matchPlayers
            .filter(mp => new Date(mp.created_at) <= new Date(matchSkinsBet.created_at))
            .map(mp => mp.player_id)
          matchSkinsResult = computeMatchSkinsMoney(roundsData, matchSkinsBet, allPlayerIds, matchSkinsBet.bet_type === 'skins_carry', matchSkinsBet.config?.gross_tiebreaker)
        }

        matchSnapshot = buildMatchSnapshot({
          matchLowNet: matchLowNetResult,
          matchLowGross: matchLowGrossResult,
          matchStableford: matchStablefordResult,
          matchSkins: matchSkinsResult,
        })
      }
    }

    updateMatch.mutate({ id: matchId, status: 'completed', final_results: matchSnapshot }, { onSuccess: () => onGoHome?.() })
  }

  const handleReopenMatch = () => {
    updateMatch.mutate({ id: matchId, status: 'active', final_results: null })
  }

  const handleDeleteMatch = () => {
    setShowDeleteConfirm(true)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        {match.status !== 'setup' && !isTeamMatchSetup && (
          <button
            onClick={onBack}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '9999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--bg-tertiary)',
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                autoFocus
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName()
                  if (e.key === 'Escape') setEditingName(false)
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  backgroundColor: 'var(--bg-tertiary)',
                  border: '1px solid var(--accent-primary)',
                  borderRadius: '0.375rem',
                  padding: '0.125rem 0.5rem',
                  color: 'var(--text-primary)',
                  outline: 'none',
                }}
              />
              <button onClick={handleSaveName} style={{ color: 'var(--accent-primary)', flexShrink: 0 }}>
                <Check size={18} />
              </button>
              <button onClick={() => setEditingName(false)} style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
                <X size={18} />
              </button>
            </div>
          ) : (
            <h1
              onClick={isOwner && match.status === 'active' ? handleStartEditName : undefined}
              style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                lineHeight: 1.2,
                cursor: isOwner && match.status === 'active' ? 'pointer' : 'default',
              }}
            >
              {match.status === 'setup'
                ? `${match.match_type === 'invitational' ? 'Invitational' : 'Golf Trip'} Setup`
                : match.match_name}
            </h1>
          )}
          {match.status !== 'setup' && (
            <span style={{
              fontSize: '0.6875rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: match.status === 'active' ? 'var(--accent-primary)' : 'var(--text-muted)',
            }}>
              {match.status}
            </span>
          )}
        </div>
        {isOwner && (match.status === 'setup' || (match.match_type === 'team_match' && !(match.sessions?.some(s => s.status === 'in_progress')))) && (
          <button
            onClick={handleDeleteMatch}
            style={{
              width: '36px',
              height: '36px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRadius: '9999px',
              color: '#ef4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
            }}
            title="Discard"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* Name input for setup trips/invitationals */}
      {isOwner && match.status === 'setup' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{
            fontSize: '0.9375rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '0.5rem',
          }}>
            {match.match_type === 'invitational' ? 'Invitational Name' : 'Trip Name'}
          </h3>
          <input
            autoFocus
            value={setupName ?? ''}
            onChange={(e) => {
              const val = e.target.value
              setSetupName(val)
              try { localStorage.setItem(`press_setup_name_${matchId}`, val) } catch { /* ignore */ }
            }}
            onBlur={() => {
              const trimmed = (setupName ?? '').trim()
              if (trimmed && trimmed !== match.match_name) {
                updateMatch.mutate({ id: matchId, match_name: trimmed })
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.target.blur()
              }
            }}
            placeholder={match.match_type === 'invitational' ? 'e.g. The Ryder Cup Classic' : 'e.g. Myrtle Beach 2026'}
            maxLength={60}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: '1.125rem',
              fontWeight: '600',
              outline: 'none',
            }}
          />
        </div>
      )}

      {/* Players / Teams section */}
      {(() => {
        const isTeamType = match.match_type === 'team_match' || match.match_type === 'invitational'
        const matchTeams = (match.match_teams || []).sort((a, b) => a.team_order - b.team_order)
        const teamsEstablished = isTeamType && matchTeams.length >= 2 && matchTeams.some((t) => (t.match_team_players || []).length > 0)

        if (teamsEstablished) {
          // ── Team View ──
          return (
            <>
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color)',
                padding: '1rem 1.25rem 1rem 1rem',
                marginBottom: '1rem',
              }}>
                {/* Header with column labels */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0 0.5rem 0.5rem',
                  marginBottom: '0.75rem',
                  borderBottom: '1px solid var(--border-color)',
                }}>
                  <h3 style={{
                    flex: 1,
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    Teams ({matchPlayers.length} players)
                  </h3>
                  <span style={{ fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '36px', textAlign: 'center' }}>HCP</span>
                  {isOwner && <span style={{ fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '26px', textAlign: 'center' }}>Scorer</span>}
                </div>

                {/* Team cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {matchTeams.map((team) => {
                    const teamPlayers = team.match_team_players || []
                    return (
                      <div key={team.id}>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: '700',
                          color: 'var(--text-primary)',
                          marginBottom: '0.375rem',
                        }}>
                          {team.team_name}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {teamPlayers.map((mtp) => {
                            const player = mtp.player
                            if (!player) return null
                            const mp = matchPlayers.find((m) => m.player_id === mtp.player_id)
                            const isEditingThis = mp && editingHandicap?.id === mp.id
                            return (
                              <div
                                key={mtp.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  padding: '0.375rem 0.5rem',
                                  backgroundColor: 'var(--bg-tertiary)',
                                  borderRadius: '0.375rem',
                                }}
                              >
                                <div style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  backgroundColor: player.avatar_color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '700',
                                  fontSize: '0.625rem',
                                  color: '#fff',
                                  flexShrink: 0,
                                }}>
                                  {getInitials(player)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <button
                                    onClick={() => onViewProfile && onViewProfile(mtp.player_id)}
                                    style={{
                                      fontWeight: '500',
                                      fontSize: '0.875rem',
                                      textAlign: 'left',
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      color: 'var(--text-primary)',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {player.display_name}
                                  </button>
                                </div>
                                {mp && isEditingThis ? (
                                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                    <PlusHandicapToggle
                                      compact
                                      isPlus={editingHandicap.isPlus}
                                      onChange={(next) => setEditingHandicap({ ...editingHandicap, isPlus: next })}
                                    />
                                    <input
                                      type="number"
                                      inputMode="decimal"
                                      min="0"
                                      step="0.1"
                                      value={editingHandicap.magnitude}
                                      onChange={(e) => setEditingHandicap({ ...editingHandicap, magnitude: e.target.value })}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.target.blur() }
                                        if (e.key === 'Escape') { setEditingHandicap(null) }
                                      }}
                                      onBlur={() => handleHandicapSave(mp.id)}
                                      autoFocus
                                      style={{
                                        width: '52px',
                                        height: '30px',
                                        textAlign: 'center',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.8125rem',
                                        fontWeight: '600',
                                        backgroundColor: 'var(--bg-secondary)',
                                        border: '1px solid var(--accent-secondary)',
                                        color: 'var(--text-primary)',
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => isOwner && mp && setEditingHandicap({ id: mp.id, ...splitHandicapForInput(mp.handicap_index) })}
                                    style={{
                                      padding: '0.125rem 0.375rem',
                                      borderRadius: '9999px',
                                      backgroundColor: isOwner ? 'var(--bg-secondary)' : 'transparent',
                                      fontSize: '0.8125rem',
                                      fontWeight: '600',
                                      color: 'var(--text-primary)',
                                      minWidth: '36px',
                                      textAlign: 'center',
                                    }}
                                    title="Handicap Index"
                                  >
                                    {mp ? formatHandicapIndex(mp.handicap_index) : '—'}
                                  </button>
                                )}
                                {isOwner && mp && (
                                  <button
                                    onClick={() => handleToggleScorer(mp)}
                                    title={mp.is_scorer ? 'Remove scorer' : 'Make scorer'}
                                    style={{
                                      width: '26px',
                                      height: '26px',
                                      borderRadius: '9999px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: mp.is_scorer ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)' : 'transparent',
                                      color: mp.is_scorer ? 'var(--accent-primary)' : 'var(--text-muted)',
                                      fontSize: '0.6875rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    S
                                  </button>
                                )}
                              </div>
                            )
                          })}
                          {teamPlayers.length === 0 && (
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem 0' }}>
                              No players yet
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Edit Players / Teams toggle button */}
                {isOwner && match.status !== 'completed' && (
                  <button
                    onClick={() => setShowTeamEditActions((v) => !v)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.375rem',
                      width: '100%',
                      padding: '0.625rem',
                      marginTop: '0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      color: showTeamEditActions ? 'var(--text-muted)' : 'var(--accent-primary)',
                      backgroundColor: 'var(--bg-tertiary)',
                      border: '1px dashed var(--border-color)',
                    }}
                  >
                    <Pencil size={14} />
                    {showTeamEditActions ? 'Done Editing' : 'Edit Players / Teams'}
                  </button>
                )}

                {/* Expanded edit actions */}
                {isOwner && match.status !== 'completed' && showTeamEditActions && (
                  <>
                    <button
                      onClick={onAddPlayers}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.375rem',
                        width: '100%',
                        padding: '0.625rem',
                        marginTop: '0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        color: 'var(--accent-primary)',
                        backgroundColor: 'var(--bg-tertiary)',
                        border: '1px dashed var(--border-color)',
                      }}
                    >
                      <Plus size={16} />
                      Add/Edit Players
                    </button>
                    <button
                      onClick={() => onSetupTeams(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.375rem',
                        width: '100%',
                        padding: '0.625rem',
                        marginTop: '0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        color: 'var(--accent-primary)',
                        backgroundColor: 'var(--bg-tertiary)',
                        border: '1px dashed var(--border-color)',
                      }}
                    >
                      <Users size={16} />
                      Edit Teams
                    </button>
                    {matchPlayers.length > 0 && !rounds.some((r) => r.status === 'in_progress') && !(match.sessions || []).some(s => s.status === 'in_progress') && (() => {
                      const ghinPlayers = matchPlayers.filter((mp) => mp.player?.ghin_number)
                      const hasGhin = ghinPlayers.length > 0
                      return (
                        <button
                          onClick={() => {
                            if (!hasGhin) return
                            batchRefresh.mutate(ghinPlayers.map((mp) => ({
                              ...mp.player,
                              match_player_id: mp.id,
                            })))
                          }}
                          disabled={batchRefresh.isPending || !hasGhin}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.375rem',
                            width: '100%',
                            padding: '0.625rem',
                            marginTop: '0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.8125rem',
                            fontWeight: '600',
                            color: !hasGhin ? 'var(--text-muted)' : batchRefresh.isPending ? 'var(--text-muted)' : 'var(--accent-secondary, #f59e0b)',
                            backgroundColor: 'var(--bg-tertiary)',
                            border: '1px dashed var(--border-color)',
                            opacity: !hasGhin || batchRefresh.isPending ? 0.5 : 1,
                          }}
                        >
                          <RefreshCw size={14} style={batchRefresh.isPending ? { animation: 'spin 1s linear infinite' } : {}} />
                          {!hasGhin
                            ? 'Sync Latest GHIN Handicaps (no GHIN numbers)'
                            : batchRefresh.isPending
                              ? 'Syncing...'
                              : batchRefresh.isSuccess
                                ? `Updated ${batchRefresh.data?.updated || 0} handicap${batchRefresh.data?.updated === 1 ? '' : 's'}`
                                : `Sync Latest GHIN Handicaps (${ghinPlayers.length})`
                          }
                        </button>
                      )
                    })()}
                  </>
                )}
              </div>

              {/* Buy-In & Payout card — shown for team_match and invitational when buy-in is set */}
              {(match.match_type === 'team_match' || match.match_type === 'invitational') && match.buy_in > 0 && (() => {
                const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
                const pot = matchPlayers.length * Number(match.buy_in)
                const splits = match.payout_config?.splits || [100]
                const numTeams = (match.match_teams || []).length
                const playersPerTeam = numTeams > 0 ? matchPlayers.length / numTeams : 1
                return (
                  <div style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '0.75rem',
                    border: '1px solid var(--border-color)',
                    padding: '1rem',
                    marginBottom: '1rem',
                  }}>
                    <h3 style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '0.75rem',
                    }}>
                      Buy-In & Payout
                    </h3>
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--border-color)',
                    }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: '600' }}>
                        ${Number(match.buy_in)} per player — ${pot} total pot
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' }}>
                        {splits.map((pct, i) => {
                          const dollarAmount = Math.round(pct / 100 * pot)
                          const perPlayer = Math.round(dollarAmount / playersPerTeam - Number(match.buy_in))
                          return (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                {ORDINALS[i]} Place
                              </span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                ${dollarAmount} ({pct}%) · {perPlayer >= 0 ? '+' : ''}${perPlayer}/player
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    {isOwner && match.status !== 'completed' && (
                      <button
                        onClick={() => onSetupBuyIn?.(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.375rem',
                          width: '100%',
                          padding: '0.625rem',
                          marginTop: '0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          color: 'var(--accent-primary)',
                          backgroundColor: 'var(--bg-tertiary)',
                          border: '1px dashed var(--border-color)',
                        }}
                      >
                        <Pencil size={14} />
                        Edit Buy-In / Payout
                      </button>
                    )}
                  </div>
                )
              })()}

              {/* Session info display — team_match only (invitational has its own Sessions section) */}
              {match.match_type === 'team_match' && match.sessions?.length > 0 && match.sessions
                .sort((a, b) => (a.session_order ?? 0) - (b.session_order ?? 0))
                .map(session => (
                <div
                  key={session.id}
                  onClick={session.status === 'in_progress' ? () => onViewSession?.(session.id) : session.status === 'completed' ? () => onViewSession?.(session.id) : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    marginTop: '0.5rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border-color)',
                    cursor: (session.status === 'in_progress' || session.status === 'completed') ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ padding: '0.75rem', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {/* Top row: status badge + chevron */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '9999px',
                        ...(session.status === 'in_progress' ? {
                          color: 'var(--accent-primary)',
                          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                        } : session.status === 'completed' ? {
                          color: 'var(--text-muted)',
                          backgroundColor: 'var(--bg-secondary)',
                        } : {
                          color: 'var(--accent-secondary, #f59e0b)',
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                        }),
                      }}>
                        {session.status === 'in_progress' ? 'Live' : session.status === 'completed' ? 'Completed' : 'Not Started'}
                      </span>
                      {(session.status === 'in_progress' || session.status === 'completed') && (
                        <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                      )}
                    </div>

                    {/* Middle: icon + session name + game type badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: session.status === 'in_progress'
                          ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)'
                          : session.status === 'completed'
                          ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)'
                          : 'var(--bg-secondary)',
                        flexShrink: 0,
                      }}>
                        {session.status === 'in_progress' ? (
                          <Play size={16} style={{ color: 'var(--accent-primary)' }} />
                        ) : session.status === 'completed' ? (
                          <Check size={16} style={{ color: 'var(--accent-primary)' }} />
                        ) : (
                          <Clock size={16} style={{ color: 'var(--text-muted)' }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                            {session.session_name || 'Session'}
                          </span>
                          {session.game_type && (
                            <GameTypeBadge gameTypeId={session.game_type} size="small" />
                          )}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
                          {session.course_name || ''}{session.tee_name ? ` — ${session.tee_name}` : ''}
                          {session.num_holes ? ` · ${session.num_holes} holes` : ''}
                          {session.wager ? ` · $${Number(session.wager)}/game` : ''}
                        </div>
                      </div>
                    </div>

                    {/* Matchups */}
                    {(session.session_groups || []).length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                        {(session.session_groups || [])
                          .sort((a, b) => (a.group_order ?? 0) - (b.group_order ?? 0))
                          .map(group => {
                            // Find the game for this group to filter players to only those in this tee time
                            const groupGame = (group.game_id ? (match.games || []).find(g => g.id === group.game_id) : null)
                              || (match.games || []).find(g => g.session_group_id === group.id)
                            // Fallback to session's groupPlayerAssignments when game lookup fails
                            const assignment = !groupGame ? (session.game_config?.groupPlayerAssignments || []).find(a => a.groupOrder === group.group_order) : null
                            const groupPlayerIds = groupGame
                              ? new Set((groupGame.game_players || []).map(gp => gp.player_id))
                              : assignment ? new Set(assignment.playerIds) : null
                            const groupTeams = (group.session_group_teams || []).map(sgt => {
                              const fullTeam = (match.match_teams || []).find(t => t.id === sgt.match_team?.id)
                              const allPlayers = (fullTeam?.match_team_players || []).map(tp => tp.player).filter(Boolean)
                              return {
                                teamName: sgt.match_team?.team_name || 'Team',
                                players: groupPlayerIds ? allPlayers.filter(p => groupPlayerIds.has(p.id)) : allPlayers,
                              }
                            })
                            if (groupTeams.length === 0) return null
                            return (
                              <div key={group.id}>
                                <div style={{
                                  fontSize: '0.6875rem',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  color: 'var(--text-muted)',
                                  marginBottom: '0.25rem',
                                }}>
                                  {group.group_name || `Matchup ${(group.group_order ?? 0) + 1}`}
                                </div>
                                <div style={{
                                  padding: '0.5rem 0.75rem',
                                  backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.04)',
                                  borderRadius: '0.375rem',
                                  border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                                }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.25rem', alignItems: 'start' }}>
                                  {groupTeams.map((team, tIdx) => {
                                    const isSecond = tIdx === 1
                                    return (
                                      <div key={tIdx} style={{ display: 'contents' }}>
                                        {tIdx === 1 && (
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            alignSelf: 'center',
                                            fontSize: '0.5625rem',
                                            fontWeight: '700',
                                            color: 'var(--text-muted)',
                                            textTransform: 'uppercase',
                                            padding: '0 0.25rem',
                                          }}>
                                            vs
                                          </div>
                                        )}
                                        <div style={{ textAlign: isSecond ? 'right' : 'left' }}>
                                          <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            color: 'var(--accent-primary)',
                                            marginBottom: '0.125rem',
                                          }}>
                                            {team.teamName}
                                          </div>
                                          {team.players.length > 0 && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', alignItems: isSecond ? 'flex-end' : 'flex-start' }}>
                                              {team.players.map((player) => (
                                                <span key={player.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexDirection: isSecond ? 'row-reverse' : 'row' }}>
                                                  <span style={{
                                                    width: '1.125rem',
                                                    height: '1.125rem',
                                                    borderRadius: '50%',
                                                    backgroundColor: player.avatar_color,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: '700',
                                                    fontSize: getInitials(player).length > 1 ? '0.375rem' : '0.5rem',
                                                    color: '#fff',
                                                    flexShrink: 0,
                                                  }}>
                                                    {getInitials(player)}
                                                  </span>
                                                  <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: isSecond ? 'flex-end' : 'flex-start' }}>
                                                    <span style={{ fontSize: '0.6875rem', fontWeight: '500', color: 'var(--text-secondary)' }}>
                                                      {player.display_name?.split(' ')[0]}
                                                    </span>
                                                    {(() => {
                                                      if (!groupGame) return null
                                                      const mt = getMixedTees(groupGame)
                                                      const playerTee = mt?.[player.id]
                                                      const defaultTee = getGameTee(groupGame)
                                                      return playerTee && playerTee.name !== defaultTee?.name ? (
                                                        <span style={{ fontSize: '0.5rem', fontWeight: '500', color: 'var(--text-muted)', lineHeight: 1 }}>
                                                          {playerTee.name}
                                                        </span>
                                                      ) : null
                                                    })()}
                                                  </span>
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    )}

                    {/* Activate / Resume Session button */}
                    {isOwner && match.status !== 'completed' && (session.status === 'setup' || session.status === 'completed') && (() => {
                      const isCompleted = session.status === 'completed'
                      return (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowActivateConfirm(session.id) }}
                          disabled={activateSessionMutation.isPending}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.375rem',
                            width: '100%',
                            padding: '0.625rem',
                            borderRadius: '9999px',
                            fontSize: '0.8125rem',
                            fontWeight: '700',
                            color: isCompleted ? 'var(--accent-secondary, #f59e0b)' : '#10b981',
                            backgroundColor: isCompleted ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.1)',
                            border: `1px solid ${isCompleted ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.3)'}`,
                            letterSpacing: '0.01em',
                          }}
                        >
                          {isCompleted ? <RotateCcw size={14} /> : <Play size={14} strokeWidth={2.5} />}
                          {isCompleted ? 'Resume Session' : 'Activate Session'}
                        </button>
                      )
                    })()}
                  </div>
                </div>
              ))}
            </>
          )
        }

        // ── Standard flat player list ──
        return (
          <>
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '0.75rem',
              border: '1px solid var(--border-color)',
              padding: '1rem 1.25rem 1rem 1rem',
              marginBottom: '1rem',
            }}>
              {/* Header row with PLAYERS title and column labels */}
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '0.75rem',
                padding: '0 0 0.5rem 0',
                marginBottom: '0.25rem',
                borderBottom: '1px solid var(--border-color)',
              }}>
                <h3 style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  Players ({matchPlayers.length})
                </h3>
                <div style={{ minWidth: '48px', textAlign: 'center', fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  HCP
                </div>
                {isOwner && (
                  <div style={{ width: '30px', textAlign: 'center', fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Scorer
                  </div>
                )}
              </div>

              {/* Player list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {matchPlayers.map((mp) => {
                  const player = mp.player
                  if (!player) return null
                  const isEditingThis = editingHandicap?.id === mp.id

                  return (
                    <div
                      key={mp.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.5rem 0',
                        borderBottom: '1px solid var(--border-color)',
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: player.avatar_color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: getInitials(player).length > 1 ? '0.625rem' : '0.8125rem',
                        color: '#fff',
                        flexShrink: 0,
                      }}>
                        {getInitials(player)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <button
                          onClick={() => onViewProfile && onViewProfile(mp.player_id)}
                          style={{
                            fontWeight: '500',
                            fontSize: '1rem',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            borderBottom: '1px dashed var(--border-color)',
                          }}
                        >
                          {player.display_name}
                        </button>
                      </div>

                      {/* Handicap */}
                      {isEditingThis ? (
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                          <PlusHandicapToggle
                            compact
                            isPlus={editingHandicap.isPlus}
                            onChange={(next) => setEditingHandicap({ ...editingHandicap, isPlus: next })}
                          />
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.1"
                            value={editingHandicap.magnitude}
                            onChange={(e) => setEditingHandicap({ ...editingHandicap, magnitude: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.target.blur() }
                              if (e.key === 'Escape') { setEditingHandicap(null) }
                            }}
                            onBlur={() => handleHandicapSave(mp.id)}
                            autoFocus
                            style={{
                              width: '60px',
                              height: '36px',
                              textAlign: 'center',
                              borderRadius: '0.375rem',
                              fontSize: '0.9375rem',
                              fontWeight: '600',
                              backgroundColor: 'var(--bg-tertiary)',
                              border: '1px solid var(--accent-secondary)',
                              color: 'var(--text-primary)',
                            }}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => isOwner && setEditingHandicap({ id: mp.id, ...splitHandicapForInput(mp.handicap_index) })}
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '9999px',
                            backgroundColor: 'var(--bg-tertiary)',
                            fontSize: '0.9375rem',
                            fontWeight: '600',
                            color: 'var(--text-primary)',
                            minWidth: '48px',
                            textAlign: 'center',
                          }}
                          title="Handicap Index"
                        >
                          {formatHandicapIndex(mp.handicap_index)}
                        </button>
                      )}

                      {/* Scorer toggle — owner cannot toggle their own scorer off */}
                      {isOwner && (
                        <button
                          onClick={() => {
                            if (mp.user_id === match.owner_id && mp.is_scorer) return
                            handleToggleScorer(mp)
                          }}
                          title={mp.user_id === match.owner_id && mp.is_scorer ? 'Owner is always a scorer' : mp.is_scorer ? 'Remove scorer' : 'Make scorer'}
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '9999px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: mp.is_scorer ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)' : 'transparent',
                            color: mp.is_scorer ? 'var(--accent-primary)' : 'var(--text-muted)',
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            opacity: mp.user_id === match.owner_id && mp.is_scorer ? 0.5 : 1,
                          }}
                        >
                          S
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>

              {matchPlayers.length === 0 && (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '1rem 0' }}>
                  No players added yet
                </p>
              )}

              {/* Add/Edit Players button */}
              {isOwner && match.status !== 'completed' && (
                <button
                  onClick={() => { flushSetupName(); onAddPlayers() }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    width: '100%',
                    padding: '0.625rem',
                    marginTop: '0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    color: 'var(--accent-primary)',
                    backgroundColor: 'var(--bg-tertiary)',
                    border: '1px dashed var(--border-color)',
                  }}
                >
                  <Plus size={16} />
                  Add/Edit Players
                </button>
              )}

              {/* Refresh Handicaps button — hidden during active rounds/sessions to protect live scoring */}
              {isOwner && match.status !== 'completed' && matchPlayers.length > 0 && !rounds.some((r) => r.status === 'in_progress') && !(match.sessions || []).some(s => s.status === 'in_progress') && (() => {
                const ghinPlayers = matchPlayers.filter((mp) => mp.player?.ghin_number)
                const hasGhin = ghinPlayers.length > 0
                return (
                  <button
                    onClick={() => {
                      if (!hasGhin) return
                      batchRefresh.mutate(ghinPlayers.map((mp) => ({
                        ...mp.player,
                        match_player_id: mp.id,
                      })))
                    }}
                    disabled={batchRefresh.isPending || !hasGhin}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.375rem',
                      width: '100%',
                      padding: '0.625rem',
                      marginTop: '0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      color: !hasGhin ? 'var(--text-muted)' : batchRefresh.isPending ? 'var(--text-muted)' : 'var(--accent-secondary, #f59e0b)',
                      backgroundColor: 'var(--bg-tertiary)',
                      border: '1px dashed var(--border-color)',
                      opacity: !hasGhin || batchRefresh.isPending ? 0.5 : 1,
                    }}
                  >
                    <RefreshCw size={14} style={batchRefresh.isPending ? { animation: 'spin 1s linear infinite' } : {}} />
                    {!hasGhin
                      ? 'Sync Latest GHIN Handicaps (no GHIN numbers)'
                      : batchRefresh.isPending
                        ? 'Syncing...'
                        : batchRefresh.isSuccess
                          ? `Updated ${batchRefresh.data?.updated || 0} handicap${batchRefresh.data?.updated === 1 ? '' : 's'}`
                          : `Sync Latest GHIN Handicaps (${ghinPlayers.length})`
                    }
                  </button>
                )
              })()}
            </div>

          </>
        )
      })()}

      {/* Side Games section — hidden for team_match (configured in session wizard) */}
      {(match.status === 'active' || match.status === 'completed') && match.match_type !== 'team_match' && (
        <SideBetsSection
          matchId={matchId}
          match={match}
          sideBets={sideBets || []}
          isOwner={isOwner}
          onOpenConfig={() => setShowSideGameConfig(true)}
        />
      )}

      {/* Rounds section — hidden for team matches (they use sessions/groups instead) */}
      {match.match_type !== 'team_match' && match.match_type !== 'invitational' && match.status !== 'setup' && (
        <RoundsSection
          rounds={rounds}
          matchPlayers={matchPlayers}
          matchStatus={match.status}
          matchType={match.match_type}
          isOwner={isOwner}
          onShowRoundWizard={() => setShowRoundWizard(true)}
          onStartRound={onStartRound}
          onEndRound={onEndRound}
          onViewRound={onViewRound}
          showResumeConfirm={showResumeConfirm}
          onResumeRound={(roundId) => setShowResumeConfirm(roundId)}
          onResumeConfirm={(roundId) => {
            resumeGame.mutate(roundId, {
              onSuccess: () => setShowResumeConfirm(null),
            })
          }}
          onResumeCancel={() => setShowResumeConfirm(null)}
          isResuming={resumeGame.isPending}
        />
      )}

      {/* Sessions section — invitational only, similar to RoundsSection for trips */}
      {match.match_type === 'invitational' && (match.status === 'active' || match.status === 'completed') && (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          padding: '1rem',
          marginBottom: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: 0,
            }}>
              Sessions ({(match.sessions || []).length})
            </h3>
            {isOwner && match.status !== 'completed' && (match.sessions || []).length >= 1 && !isReorderingSessions && (
              <button
                onClick={() => setIsReorderingSessions(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.25rem',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  borderRadius: '9999px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                title="Reorder sessions"
              >
                <Pencil size={14} />
              </button>
            )}
            {isReorderingSessions && (
              <button
                onClick={() => setIsReorderingSessions(false)}
                style={{
                  background: 'none',
                  border: '1px solid var(--border-color)',
                  padding: '0.25rem 0.625rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  borderRadius: '9999px',
                }}
              >
                Done
              </button>
            )}
          </div>

          {isOwner && match.status === 'active' && !((match.match_type === 'team_match' || match.match_type === 'invitational') && !((match.match_teams || []).length >= 2 && (match.match_teams || []).some(t => (t.match_team_players || []).length > 0))) && (
            <button
              onClick={() => setShowSessionWizard(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                width: '100%',
                padding: '0.875rem',
                marginBottom: '0.75rem',
                borderRadius: '9999px',
                fontSize: '0.9375rem',
                fontWeight: '700',
                color: '#fff',
                backgroundColor: '#10b981',
                border: 'none',
                letterSpacing: '0.01em',
              }}
            >
              <Plus size={18} strokeWidth={2.5} />
              Add New Session
            </button>
          )}

          {(match.sessions || []).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '1rem 0' }}>
              {labels.noRoundsHint}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {(match.sessions || []).sort((a, b) => (a.session_order ?? 0) - (b.session_order ?? 0)).map(session => (
                <div
                  key={session.id}
                  draggable={isReorderingSessions}
                  onDragStart={isReorderingSessions ? (e) => {
                    setDraggedSessionId(session.id)
                    e.dataTransfer.effectAllowed = 'move'
                  } : undefined}
                  onDragOver={isReorderingSessions ? (e) => {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                  } : undefined}
                  onDrop={isReorderingSessions ? (e) => {
                    e.preventDefault()
                    if (!draggedSessionId || draggedSessionId === session.id) return
                    const sessions = [...(match.sessions || [])].sort((a, b) => (a.session_order ?? 0) - (b.session_order ?? 0))
                    const fromIndex = sessions.findIndex(s => s.id === draggedSessionId)
                    const toIndex = sessions.findIndex(s => s.id === session.id)
                    if (fromIndex === -1 || toIndex === -1) return
                    const [moved] = sessions.splice(fromIndex, 1)
                    sessions.splice(toIndex, 0, moved)
                    const updates = sessions.map((s, i) => ({ id: s.id, session_order: i }))
                    reorderSessionsMutation.mutate({ updates, match_id: match.id }, {
                      onSuccess: () => refetch(),
                    })
                    setDraggedSessionId(null)
                  } : undefined}
                  onDragEnd={() => setDraggedSessionId(null)}
                  onClick={!isReorderingSessions ? (session.status === 'in_progress' ? () => onViewSession?.(session.id) : session.status === 'completed' ? () => onViewSession?.(session.id) : undefined) : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRadius: '0.5rem',
                    border: draggedSessionId === session.id ? '1px dashed var(--text-muted)' : '1px solid var(--border-color)',
                    opacity: draggedSessionId === session.id ? 0.5 : 1,
                    cursor: isReorderingSessions ? 'grab' : (session.status === 'in_progress' || session.status === 'completed') ? 'pointer' : 'default',
                  }}
                >
                  {isReorderingSessions && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 0.375rem 0 0.5rem',
                      color: 'var(--text-muted)',
                      flexShrink: 0,
                    }}>
                      <GripVertical size={16} />
                    </div>
                  )}
                  <div style={{ padding: '0.75rem', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {/* Top row: status badge + chevron */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '9999px',
                        ...(session.status === 'in_progress' ? {
                          color: 'var(--accent-primary)',
                          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                        } : session.status === 'completed' ? {
                          color: 'var(--text-muted)',
                          backgroundColor: 'var(--bg-secondary)',
                        } : {
                          color: 'var(--accent-secondary, #f59e0b)',
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                        }),
                      }}>
                        {session.status === 'in_progress' ? 'Live' : session.status === 'completed' ? 'Completed' : 'Not Started'}
                      </span>
                      {(session.status === 'in_progress' || session.status === 'completed') && !isReorderingSessions && (
                        <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                      )}
                    </div>

                    {/* Middle: icon + session name + game type badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: session.status === 'in_progress'
                          ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)'
                          : session.status === 'completed'
                          ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)'
                          : 'var(--bg-secondary)',
                        flexShrink: 0,
                      }}>
                        {session.status === 'in_progress' ? (
                          <Play size={16} style={{ color: 'var(--accent-primary)' }} />
                        ) : session.status === 'completed' ? (
                          <Check size={16} style={{ color: 'var(--accent-primary)' }} />
                        ) : (
                          <Clock size={16} style={{ color: 'var(--text-muted)' }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                            {session.session_name || 'Session'}
                          </span>
                          {session.game_type && (
                            <GameTypeBadge gameTypeId={session.game_type} size="small" />
                          )}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
                          {session.course_name || ''}{session.tee_name ? ` — ${session.tee_name}` : ''}
                          {session.num_holes ? ` · ${session.num_holes} holes` : ''}
                          {session.wager ? ` · $${Number(session.wager)}/game` : ''}
                        </div>
                      </div>
                    </div>

                    {/* Matchups */}
                    {(() => {
                      const groups = (session.session_groups || [])
                      const completedMatchups = groups.filter(g => {
                        const gGame = (g.game_id ? (match.games || []).find(ga => ga.id === g.game_id) : null)
                          || (match.games || []).find(ga => ga.session_group_id === g.id)
                        return gGame?.status === 'completed'
                      }).length
                      const totalMatchups = groups.length
                      const allMatchupsComplete = totalMatchups > 0 && completedMatchups === totalMatchups
                      return (
                        <>
                    {totalMatchups > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                        {groups
                          .sort((a, b) => (a.group_order ?? 0) - (b.group_order ?? 0))
                          .map(group => {
                            // Find the game for this group to filter players to only those in this tee time
                            const groupGame = (group.game_id ? (match.games || []).find(g => g.id === group.game_id) : null)
                              || (match.games || []).find(g => g.session_group_id === group.id)
                            // Fallback to session's groupPlayerAssignments when game lookup fails
                            const assignment = !groupGame ? (session.game_config?.groupPlayerAssignments || []).find(a => a.groupOrder === group.group_order) : null
                            const groupPlayerIds = groupGame
                              ? new Set((groupGame.game_players || []).map(gp => gp.player_id))
                              : assignment ? new Set(assignment.playerIds) : null
                            const groupTeams = (group.session_group_teams || []).map(sgt => {
                              const fullTeam = (match.match_teams || []).find(t => t.id === sgt.match_team?.id)
                              const allPlayers = (fullTeam?.match_team_players || []).map(tp => tp.player).filter(Boolean)
                              return {
                                teamName: sgt.match_team?.team_name || 'Team',
                                players: groupPlayerIds ? allPlayers.filter(p => groupPlayerIds.has(p.id)) : allPlayers,
                              }
                            })
                            if (groupTeams.length === 0) return null
                            const isMatchupComplete = groupGame?.status === 'completed'
                            return (
                              <div key={group.id}>
                                <div style={{
                                  fontSize: '0.6875rem',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  color: 'var(--text-muted)',
                                  marginBottom: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                }}>
                                  {group.group_name || `Matchup ${(group.group_order ?? 0) + 1}`}
                                  {session.status === 'in_progress' && isMatchupComplete && (
                                    <span style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.125rem',
                                      fontSize: '0.5625rem',
                                      fontWeight: '700',
                                      color: 'var(--accent-primary)',
                                      backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.12)',
                                      padding: '0.0625rem 0.375rem',
                                      borderRadius: '9999px',
                                      textTransform: 'uppercase',
                                    }}>
                                      <Check size={10} />
                                      Complete
                                    </span>
                                  )}
                                </div>
                                <div style={{
                                  padding: '0.5rem 0.75rem',
                                  backgroundColor: isMatchupComplete
                                    ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.02)'
                                    : 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.04)',
                                  borderRadius: '0.375rem',
                                  border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                                  opacity: isMatchupComplete ? 0.7 : 1,
                                }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.25rem', alignItems: 'start' }}>
                                  {groupTeams.map((team, tIdx) => {
                                    const isSecond = tIdx === 1
                                    return (
                                      <div key={tIdx} style={{ display: 'contents' }}>
                                        {tIdx === 1 && (
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            alignSelf: 'center',
                                            fontSize: '0.5625rem',
                                            fontWeight: '700',
                                            color: 'var(--text-muted)',
                                            textTransform: 'uppercase',
                                            padding: '0 0.25rem',
                                          }}>
                                            vs
                                          </div>
                                        )}
                                        <div style={{ textAlign: isSecond ? 'right' : 'left' }}>
                                          <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            color: 'var(--accent-primary)',
                                            marginBottom: '0.125rem',
                                          }}>
                                            {team.teamName}
                                          </div>
                                          {team.players.length > 0 && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', alignItems: isSecond ? 'flex-end' : 'flex-start' }}>
                                              {team.players.map((player) => (
                                                <span key={player.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexDirection: isSecond ? 'row-reverse' : 'row' }}>
                                                  <span style={{
                                                    width: '1.125rem',
                                                    height: '1.125rem',
                                                    borderRadius: '50%',
                                                    backgroundColor: player.avatar_color,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: '700',
                                                    fontSize: getInitials(player).length > 1 ? '0.375rem' : '0.5rem',
                                                    color: '#fff',
                                                    flexShrink: 0,
                                                  }}>
                                                    {getInitials(player)}
                                                  </span>
                                                  <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: isSecond ? 'flex-end' : 'flex-start' }}>
                                                    <span style={{ fontSize: '0.6875rem', fontWeight: '500', color: 'var(--text-secondary)' }}>
                                                      {player.display_name?.split(' ')[0]}
                                                    </span>
                                                    {(() => {
                                                      if (!groupGame) return null
                                                      const mt = getMixedTees(groupGame)
                                                      const playerTee = mt?.[player.id]
                                                      const defaultTee = getGameTee(groupGame)
                                                      return playerTee && playerTee.name !== defaultTee?.name ? (
                                                        <span style={{ fontSize: '0.5rem', fontWeight: '500', color: 'var(--text-muted)', lineHeight: 1 }}>
                                                          {playerTee.name}
                                                        </span>
                                                      ) : null
                                                    })()}
                                                  </span>
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    )}

                    {/* End Session button for live sessions (owner only) */}
                    {isOwner && session.status === 'in_progress' && !isReorderingSessions && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (allMatchupsComplete) {
                            deactivateSessionMutation.mutateAsync({ id: session.id, match_id: match.id, mode: 'complete' })
                          }
                        }}
                        disabled={!allMatchupsComplete || deactivateSessionMutation.isPending}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.375rem',
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: allMatchupsComplete ? 'var(--accent-primary)' : 'var(--text-muted)',
                          backgroundColor: allMatchupsComplete
                            ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)'
                            : 'var(--bg-tertiary)',
                          border: allMatchupsComplete
                            ? '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.2)'
                            : '1px solid var(--border-color)',
                          opacity: allMatchupsComplete ? 1 : 0.6,
                          cursor: allMatchupsComplete ? 'pointer' : 'default',
                        }}
                      >
                        {deactivateSessionMutation.isPending ? (
                          <>
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid var(--border-color)',
                              borderTopColor: 'var(--accent-primary)',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                            }} />
                            Ending Session...
                          </>
                        ) : (
                          <>
                            <Trophy size={14} />
                            {allMatchupsComplete
                              ? 'End Session'
                              : `End Session (${completedMatchups}/${totalMatchups} complete)`}
                          </>
                        )}
                      </button>
                    )}
                        </>
                      )
                    })()}

                    {/* Edit Session button — visible in reorder mode for setup sessions */}
                    {isReorderingSessions && session.status === 'setup' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingSession(session); setIsReorderingSessions(false) }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.375rem',
                          width: '100%',
                          padding: '0.625rem',
                          borderRadius: '9999px',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          color: 'var(--accent-primary)',
                          backgroundColor: 'var(--bg-tertiary)',
                          border: '1px dashed var(--border-color)',
                          cursor: 'pointer',
                        }}
                      >
                        <Pencil size={14} />
                        Edit Session
                      </button>
                    )}

                    {/* Activate / Resume Session button */}
                    {isOwner && match.status !== 'completed' && (session.status === 'setup' || session.status === 'completed') && !isReorderingSessions && (() => {
                      const isCompleted = session.status === 'completed'
                      return (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowActivateConfirm(session.id) }}
                          disabled={activateSessionMutation.isPending}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.375rem',
                            width: '100%',
                            padding: '0.625rem',
                            borderRadius: '9999px',
                            fontSize: '0.8125rem',
                            fontWeight: '700',
                            color: isCompleted ? 'var(--accent-secondary, #f59e0b)' : '#10b981',
                            backgroundColor: isCompleted ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.1)',
                            border: `1px solid ${isCompleted ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.3)'}`,
                            letterSpacing: '0.01em',
                          }}
                        >
                          {isCompleted ? <RotateCcw size={14} /> : <Play size={14} strokeWidth={2.5} />}
                          {isCompleted ? 'Resume Session' : 'Activate Session'}
                        </button>
                      )
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      )}

      {/* Set Up Teams / Continue to Team Setup — hidden once teams are established */}
      {isOwner && match.match_type === 'team_match' && match.status === 'active' && !((match.match_teams || []).length >= 2 && (match.match_teams || []).some(t => (t.match_team_players || []).length > 0)) && (
        <button
          onClick={onSetupTeams}
          disabled={matchPlayers.length < 2}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '0.875rem',
            marginTop: '1rem',
            marginBottom: '0.5rem',
            borderRadius: '9999px',
            fontSize: '0.9375rem',
            fontWeight: '700',
            color: '#fff',
            backgroundColor: '#10b981',
            border: 'none',
            letterSpacing: '0.01em',
            opacity: (matchPlayers.length < 2) ? 0.5 : 1,
          }}
        >
          <Users size={18} strokeWidth={2.5} />
          Continue to Team Setup
        </button>
      )}

      {/* Discard button for team match initial setup */}
      {isTeamMatchSetup && (
        <button
          onClick={handleDeleteMatch}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            width: '100%',
            padding: '0.75rem',
            marginTop: '0.5rem',
            borderRadius: '9999px',
            fontSize: '0.8125rem',
            fontWeight: '600',
            color: '#ef4444',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}
        >
          <Trash2 size={16} />
          Discard
        </button>
      )}

      {/* Continue to Session Setup — team_match bottom CTA */}
      {isOwner && match.match_type === 'team_match' && !(match.sessions?.length > 0) && (
        <>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setShowSessionWizard(true)}
          style={{
            width: '100%',
            padding: '1rem',
            marginTop: '1.5rem',
            borderRadius: '9999px',
            fontSize: '1rem',
            fontWeight: '700',
            color: '#fff',
            backgroundColor: '#10b981',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
        >
          Continue to Session Setup
        </button>
        </>
      )}

      {/* Finish Match button */}
      {isOwner && match.status === 'active' && (
        <button
          onClick={() => {
            // Check for incomplete rounds or active sessions
            const hasIncompleteRounds = rounds.some(g => g.status !== 'completed')
            const hasActiveSessions = (match.sessions || []).some(s => s.status !== 'completed')
            if (hasIncompleteRounds || hasActiveSessions) {
              setHasIncompleteGames(true)
            } else {
              setHasIncompleteGames(false)
            }
            setShowFinishConfirm(true)
          }}
          style={{
            width: '100%',
            padding: '1rem',
            marginTop: '1.5rem',
            marginBottom: '1rem',
            borderRadius: '9999px',
            fontSize: '1rem',
            fontWeight: '700',
            color: '#fff',
            backgroundColor: 'var(--accent-primary)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
        >
          <Trophy size={18} />
          {labels.finishLabel}
        </button>
      )}

      {/* Finish Match confirmation modal */}
      {showFinishConfirm && (
        <div
          onClick={() => setShowFinishConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            {hasIncompleteGames ? (
              <>
                {(() => {
                  const isSessionType = match.match_type === 'team_match' || match.match_type === 'invitational'
                  const unitLabel = isSessionType ? 'Sessions' : 'Rounds'
                  return (
                    <>
                      <div style={{
                        backgroundColor: 'rgba(234, 179, 8, 0.12)',
                        border: '1px solid rgba(234, 179, 8, 0.3)',
                        borderRadius: '0.75rem',
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                      }}>
                        <AlertTriangle size={16} style={{ color: '#eab308', flexShrink: 0 }} />
                        <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#eab308', margin: 0 }}>
                          Unfinished {unitLabel}
                        </p>
                      </div>
                      <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1.25rem', textAlign: 'center' }}>
                        Complete all {unitLabel.toLowerCase()} first to finish {labels.singular}
                      </p>
                    </>
                  )
                })()}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => setShowFinishConfirm(false)}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      borderRadius: '9999px',
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Go Back
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowFinishConfirm(false)
                    setShowDeleteConfirm(true)
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    marginTop: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    width: '100%',
                    opacity: 0.7,
                  }}
                >
                  <Trash2 size={12} />
                  Discard {labels.singular}
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
                  {labels.finishConfirmTitle}
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
                  {labels.finishConfirmBody}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setShowFinishConfirm(false)
                      handleCompleteMatch()
                    }}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      fontWeight: '600',
                      borderRadius: '9999px',
                      color: '#fff',
                      backgroundColor: 'var(--accent-primary)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {labels.finishConfirmButton}
                  </button>
                  <button
                    onClick={() => setShowFinishConfirm(false)}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      borderRadius: '9999px',
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowFinishConfirm(false)
                    setShowDeleteConfirm(true)
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    marginTop: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    width: '100%',
                    opacity: 0.7,
                  }}
                >
                  <Trash2 size={12} />
                  Discard {labels.singular}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Activate/Resume Session confirmation + loading modal */}
      {(showActivateConfirm || activateSessionMutation.isPending) && (
        <div
          onClick={activateSessionMutation.isPending ? undefined : () => setShowActivateConfirm(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            {activateSessionMutation.isPending ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '1rem 0' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid var(--border-color)',
                  borderTopColor: 'var(--accent-primary)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <p style={{ fontSize: '0.9375rem', fontWeight: '600', color: 'var(--text-secondary)', textAlign: 'center' }}>
                  {(() => {
                    const confirmSession = (match.sessions || []).find(s => s.id === showActivateConfirm)
                    return confirmSession?.status === 'completed' ? 'Resuming Session...' : 'Activating Session...'
                  })()}
                </p>
              </div>
            ) : (
              <>
                {(() => {
                  const confirmSession = (match.sessions || []).find(s => s.id === showActivateConfirm)
                  const isResuming = confirmSession?.status === 'completed'
                  return (
                    <>
                      <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
                        {isResuming ? 'Resume Session?' : 'Activate Session?'}
                      </p>
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
                        {isResuming
                          ? 'This will reopen scoring for all matchup groups in this session. Previous scores will be cleared and new games created.'
                          : 'This will open scoring for all matchup groups in this session. Only one session can be active at a time.'}
                      </p>
                    </>
                  )
                })()}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      // For team matches, reopen the match if it was completed (single-session auto-complete)
                      if (match.match_type === 'team_match' && match.status === 'completed') {
                        updateMatch.mutate({ id: matchId, status: 'active', final_results: null })
                      }
                      activateSessionMutation.mutate(
                        { id: showActivateConfirm, match_id: match.id },
                        { onSuccess: () => { setShowActivateConfirm(null); refetch(); onNavigate?.('rounds') } }
                      )
                    }}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      fontWeight: '600',
                      borderRadius: '9999px',
                      color: '#fff',
                      backgroundColor: '#10b981',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    {(() => {
                      const confirmSession = (match.sessions || []).find(s => s.id === showActivateConfirm)
                      const isResuming = confirmSession?.status === 'completed'
                      return (
                        <>
                          {isResuming ? <RotateCcw size={16} /> : <Play size={16} />}
                          {isResuming ? 'Resume Session' : 'Activate Session'}
                        </>
                      )
                    })()}
                  </button>
                  <button
                    onClick={() => setShowActivateConfirm(null)}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      borderRadius: '9999px',
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Spacer for fixed bottom button on setup matches */}
      {isOwner && match.status === 'setup' && (
        <div style={{ height: '80px' }} />
      )}

      {/* Fixed bottom activate button for setup matches */}
      {isOwner && match.status === 'setup' && (() => {
        const localName = (setupName ?? '').trim()
        const nameEntered = localName.length > 0
        const canActivate = matchPlayers.length >= 2 && nameEntered
        const isInvitational = match.match_type === 'invitational'
        const invitationalTeamsEstablished = isInvitational && (match.match_teams || []).length >= 2 && (match.match_teams || []).some(t => (t.match_team_players || []).length > 0)
        const invitationalBuyInSet = isInvitational && match.buy_in > 0
        const showTeamSetup = isInvitational && !invitationalTeamsEstablished
        const showBuyIn = isInvitational && invitationalTeamsEstablished && !invitationalBuyInSet
        return (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '1rem 1rem max(1rem, calc(0.25rem + var(--bottom-safe-area, 0px)))',
            backgroundColor: 'var(--bg-primary)',
            borderTop: '1px solid var(--border-color)',
            zIndex: 101,
          }}>
            {(() => {
              const typeLabel = isInvitational ? 'Invitational' : 'Trip'
              const needsName = !nameEntered
              const needsPlayers = matchPlayers.length < 2
              const hint = needsName
                ? { text: `Add a ${typeLabel} name to continue`, color: '#ef4444' }
                : needsPlayers
                ? { text: 'Add players to continue', color: '#ef4444' }
                : { text: 'Confirm handicaps before continuing', color: '#5e9be6' }
              return (
                <p style={{ color: hint.color, fontSize: '0.75rem', textAlign: 'center', marginBottom: '0.5rem' }}>
                  {hint.text}
                </p>
              )
            })()}
            <button
              className="btn-primary"
              onClick={() => { flushSetupName(); (showTeamSetup ? onSetupTeams : showBuyIn ? onSetupBuyIn : handleActivateMatch)() }}
              disabled={!canActivate}
              style={{
                width: '100%',
                height: '48px',
                fontSize: '0.9375rem',
                opacity: canActivate ? 1 : 0.5,
              }}
            >
              {showTeamSetup ? 'Continue to Team Setup' : showBuyIn ? 'Continue to Buy-In' : labels.activateLabel}
            </button>
          </div>
        )
      })()}

      {/* Bottom action for completed matches */}
      {isOwner && match.status === 'completed' && (
        <button
          onClick={() => setShowReopenConfirm(true)}
          style={{
            width: '100%',
            height: '44px',
            marginTop: '1.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            borderRadius: '9999px',
            color: 'var(--accent-secondary, #f59e0b)',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
          }}
        >
          <RotateCcw size={14} />
          {labels.reopenLabel}
        </button>
      )}

      {/* Reopen Match confirmation modal */}
      {showReopenConfirm && (
        <div
          onClick={() => setShowReopenConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              {labels.reopenConfirmTitle}
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
              {labels.reopenConfirmBody}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => {
                  setShowReopenConfirm(false)
                  handleReopenMatch()
                }}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  color: '#fff',
                  backgroundColor: 'var(--accent-primary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {labels.reopenConfirmButton}
              </button>
              <button
                onClick={() => setShowReopenConfirm(false)}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  borderRadius: '9999px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div
          onClick={() => setShowDeleteConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              {match.status === 'setup' || isTeamMatchSetup ? 'Discard' : 'Delete'} {labels.singular}?
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
              {(match.status === 'setup' || isTeamMatchSetup) ? 'This cannot be undone.' : labels.deleteConfirmBody}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => {
                  if (!onlineManager.isOnline()) {
                    logger.capture(new Error('Match discard blocked — offline'), {
                      matchId, matchType: match.match_type, matchStatus: match.status, source: 'MatchDashboard',
                    })
                    alert('Unable to discard — no network connection. Please try again when connectivity is restored.')
                    return
                  }
                  deleteMatch.mutate(matchId, {
                    onSuccess: () => {
                      setShowDeleteConfirm(false)
                      try { localStorage.removeItem(`press_setup_name_${matchId}`) } catch { /* ignore */ }
                      ;(onDeletedMatch || onGoHome)?.()
                    },
                    onError: (err) => {
                      logger.capture(new Error('Match discard failed: ' + err?.message), {
                        matchId, matchType: match.match_type, matchStatus: match.status, source: 'MatchDashboard',
                        isOnline: onlineManager.isOnline(),
                      })
                      alert('Failed to discard. Please check your connection and try again.')
                    },
                  })
                }}
                disabled={deleteMatch.isPending}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  color: '#fff',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  cursor: 'pointer',
                  opacity: deleteMatch.isPending ? 0.6 : 1,
                }}
              >
                {deleteMatch.isPending ? 'Deleting...' : (match.status === 'setup' || isTeamMatchSetup ? 'Discard' : 'Delete')}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteMatch.isPending}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  borderRadius: '9999px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Round Setup Wizard */}
      {showRoundWizard && (
        <RoundSetupWizard
          matchId={matchId}
          initialState={(() => {
            try {
              const raw = localStorage.getItem(`press_wizard_${matchId}`)
              if (raw) {
                const parsed = JSON.parse(raw)
                if (parsed?.phase === 'round-setup') return parsed
              }
            } catch { /* ignore */ }
            return null
          })()}
          onClose={() => {
            if (match.match_type === 'quick_round' && rounds.length === 0) {
              deleteMatch.mutate(matchId, {
                onSuccess: () => { try { localStorage.removeItem(`press_setup_name_${matchId}`) } catch { /* ignore */ }; (onDeletedMatch || onGoHome)?.() },
                onError: (err) => {
                  logger.capture(err, { action: 'deleteMatch_wizardClose', matchId, matchType: match.match_type })
                },
              })
              return
            }
            setShowRoundWizard(false)
          }}
          onRoundCreated={() => {
            setShowRoundWizard(false)
            onStartRound()
          }}
        />
      )}

      {/* Session Setup Wizard (team match) */}
      {showSessionWizard && (
        <SessionSetupWizard
          matchId={matchId}
          skipAnimation={sessionWizardRestored}
          onClose={() => { setShowSessionWizard(false); setSessionWizardRestored(false); onSessionWizardClosed?.() }}
          onSessionCreated={() => {
            onSessionWizardCompleted?.()
            setShowSessionWizard(false)
            setSessionWizardRestored(false)
            if (match.match_type === 'team_match') {
              onNavigate?.('rounds')
            }
          }}
        />
      )}

      {/* Edit Session Wizard */}
      {editingSession && (
        <SessionSetupWizard
          matchId={matchId}
          existingSession={editingSession}
          onClose={() => setEditingSession(null)}
          onSessionCreated={() => setEditingSession(null)}
        />
      )}

      {/* Side Game Config */}
      {showSideGameConfig && (
        <SideGameConfig
          matchId={matchId}
          match={match}
          sideBets={sideBets || []}
          onCreateSideBet={(bet) => createSideBet.mutate(bet)}
          onDeleteSideBet={(id) => deleteSideBet.mutate(id)}
          isCreating={createSideBet.isPending}
          onClose={() => setShowSideGameConfig(false)}
        />
      )}

    </div>
  )
}

const SIDE_GAME_ICONS = { low_net: TrendingDown, low_gross: Target, stableford: Star, skins: DollarSign, skins_carry: DollarSign }
const SIDE_GAME_NAMES = { low_net: 'Low Net', low_gross: 'Low Gross', stableford: 'Stableford', skins: 'Skins', skins_carry: 'Skins' }

function SideBetsSection({ matchId, match, sideBets, isOwner, onOpenConfig }) {
  const labels = getMatchTypeLabels(match?.match_type)
  const matchLevelBets = sideBets.filter((b) => !b.game_id && b.scope === 'match')
  if (!isOwner && matchLevelBets.length === 0) return null
  if (match?.match_type === 'quick_round') return null

  const scorableGames = useMemo(
    () => (match?.games || []).filter(g => g.status === 'completed' || g.status === 'in_progress'),
    [match?.games]
  )

  // Compute leader for each side game
  const leaders = useMemo(() => {
    if (!matchLevelBets.length || !scorableGames.length || !match?.match_players?.length) return {}

    const result = {}
    for (const bet of matchLevelBets) {
      const isSkins = bet.bet_type === 'skins' || bet.bet_type === 'skins_carry'

      // Skins: use computeMatchSkinsMoney for leader
      if (isSkins) {
        const roundsData = scorableGames.map(game => {
          const course = getGameCourse(game)
          const tee = getGameTee(game)
          const gamePlayers = game.game_players || []
          const playerIds = gamePlayers.map(gp => gp.player_id)
          const handicapBreakdown = buildSideBetHandicapBreakdown({
            matchPlayers: match.match_players, gamePlayers, course, tee, mixedTees: getMixedTees(game),
          })
          return { scores: game.scores || [], handicapBreakdown, numHoles: game.num_holes || 18, playerIds }
        }).filter(r => r.handicapBreakdown)

        if (roundsData.length > 0) {
          const allPlayerIds = [...new Set(roundsData.flatMap(r => r.playerIds))]
          const skinsResult = computeMatchSkinsMoney(roundsData, bet, allPlayerIds, bet.bet_type === 'skins_carry', bet.config?.gross_tiebreaker)
          const maxSkins = Math.max(...Object.values(skinsResult.skinsByPlayer))
          if (maxSkins > 0) {
            const winnerIds = Object.entries(skinsResult.skinsByPlayer).filter(([, v]) => v === maxSkins).map(([id]) => id)
            const winnerPlayers = winnerIds.map(id => match.match_players.find(mp => mp.player_id === id)?.player).filter(Boolean)
            if (winnerPlayers.length > 0) {
              const names = winnerPlayers.map(p => p.display_name).join(', ')
              result[bet.bet_type] = `${names} (${maxSkins})`
            }
          }
        }
        continue
      }

      const playerScores = new Map()

      for (const game of scorableGames) {
        const course = getGameCourse(game)
        const tee = getGameTee(game)
        const gamePlayers = game.game_players || []
        const scores = game.scores || []
        const numHoles = game.num_holes || 18

        const handicapBreakdown = (bet.bet_type === 'low_net' || bet.bet_type === 'stableford')
          ? buildSideBetHandicapBreakdown({
              matchPlayers: match.match_players, gamePlayers, course, tee, mixedTees: getMixedTees(game),
            })
          : null

        const holePars = course ? course.par.slice(0, numHoles) : null

        for (const gp of gamePlayers) {
          const pid = gp.player_id
          let value = 0
          let holesPlayed = 0
          for (let hole = 1; hole <= numHoles; hole++) {
            const s = scores.find(sc => sc.player_id === pid && sc.hole_number === hole)
            if (s?.strokes != null) {
              if (bet.bet_type === 'low_net') {
                const hcp = handicapBreakdown?.find(h => h.playerId === pid)
                value += s.strokes - (hcp?.strokeHoles?.get(hole) || 0)
              } else if (bet.bet_type === 'low_gross') {
                value += s.strokes
              } else if (bet.bet_type === 'stableford') {
                const hcp = handicapBreakdown?.find(h => h.playerId === pid)
                const net = s.strokes - (hcp?.strokeHoles?.get(hole) || 0)
                const par = holePars?.[hole - 1]
                const pts = stablefordPoints(net, par)
                if (pts != null) value += pts
              }
              holesPlayed++
            }
          }
          if (holesPlayed === numHoles) {
            if (!playerScores.has(pid)) playerScores.set(pid, 0)
            playerScores.set(pid, playerScores.get(pid) + value)
          }
        }
      }

      if (playerScores.size === 0) continue

      const entries = Array.from(playerScores.entries())
      const bestValue = bet.bet_type === 'stableford'
        ? Math.max(...entries.map(([, v]) => v))
        : Math.min(...entries.map(([, v]) => v))
      const winnerIds = entries.filter(([, v]) => v === bestValue).map(([id]) => id)
      const winnerPlayers = winnerIds
        .map(id => match.match_players.find(mp => mp.player_id === id)?.player)
        .filter(Boolean)

      if (winnerPlayers.length > 0) {
        result[bet.bet_type] = winnerPlayers.length === 1
          ? winnerPlayers[0].display_name
          : winnerPlayers.map(p => p.display_name).join(', ')
      }
    }
    return result
  }, [matchLevelBets, scorableGames, match?.match_players])

  return (
    <div style={{
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '0.75rem',
      border: '1px solid var(--border-color)',
      padding: '1rem',
      marginBottom: '1rem',
    }}>
      <h3 style={{
        fontSize: '0.75rem',
        fontWeight: '600',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '0.75rem',
      }}>
        {labels.sideBetsLabel}
      </h3>

      {/* Side game cards */}
      {matchLevelBets.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: isOwner && match.status !== 'completed' ? '0.75rem' : 0 }}>
          {matchLevelBets.map((bet) => {
            const Icon = SIDE_GAME_ICONS[bet.bet_type] || TrendingDown
            const name = SIDE_GAME_NAMES[bet.bet_type] || bet.bet_type
            const leader = leaders[bet.bet_type]
            return (
              <div key={bet.id} style={{
                padding: '0.75rem',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Icon size={14} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {name} — ${Number(bet.amount)}/player {labels.entireSuffix}
                  </span>
                </div>
                {leader && (
                  <div style={{ marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Trophy size={12} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                      {leader}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Owner: manage button */}
      {isOwner && match.status !== 'completed' && (
        <button
          onClick={onOpenConfig}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            width: '100%',
            padding: '0.625rem',
            borderRadius: '9999px',
            fontSize: '0.8125rem',
            fontWeight: '600',
            color: 'var(--accent-primary)',
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px dashed var(--border-color)',
            cursor: 'pointer',
          }}
        >
          <Pencil size={14} />
          {matchLevelBets.length > 0 ? 'Manage Side Games' : 'Add Side Game'}
        </button>
      )}
    </div>
  )
}

function RoundsSection({ rounds, matchPlayers, matchStatus, matchType, isOwner, onShowRoundWizard, onStartRound, onEndRound, onViewRound, showResumeConfirm, onResumeRound, onResumeConfirm, onResumeCancel, isResuming }) {
  const labels = getMatchTypeLabels(matchType)
  const liveRounds = rounds.filter((r) => r.status === 'in_progress')
  const setupRounds = rounds.filter((r) => r.status === 'setup')
  const completedRounds = rounds.filter((r) => r.status === 'completed').sort((a, b) => b.round_number - a.round_number)
  const activeRounds = [...liveRounds, ...setupRounds]

  return (
    <div style={{ marginBottom: '1rem' }}>
      <h3 style={{
        fontSize: '0.75rem',
        fontWeight: '600',
        color: 'var(--text-primary)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '0.75rem',
      }}>
        Rounds ({rounds.length})
      </h3>

      {isOwner && matchStatus === 'active' && (matchType === 'trip' || rounds.length === 0) && liveRounds.length === 0 && (
        <button
          onClick={onShowRoundWizard}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '0.875rem',
            marginBottom: '0.75rem',
            borderRadius: '9999px',
            fontSize: '0.9375rem',
            fontWeight: '700',
            color: '#fff',
            backgroundColor: '#10b981',
            border: 'none',
            letterSpacing: '0.01em',
          }}
        >
          <Plus size={18} strokeWidth={2.5} />
          Start New Round
        </button>
      )}

      {rounds.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '1rem 0' }}>
          {matchStatus === 'setup'
            ? labels.activateHint
            : labels.noRoundsHint
          }
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Live & setup rounds first */}
          {activeRounds.map((round, idx) => (
            <RoundItem
              key={round.id}
              round={round}
              index={idx}
              matchPlayers={matchPlayers}
              isOwner={isOwner}
              onStartRound={onStartRound}
              onEndRound={onEndRound}
              onViewRound={onViewRound}
            />
          ))}

          {/* Note: finish active round before starting a new one */}
          {isOwner && matchStatus === 'active' && (matchType === 'trip' || rounds.length === 0) && liveRounds.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: '0.625rem',
              borderRadius: '0.5rem',
              fontSize: '0.8125rem',
              fontWeight: '600',
              color: 'var(--text-muted)',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px dashed var(--border-color)',
              opacity: 0.6,
            }}>
              Finish the active round before starting a new one
            </div>
          )}

          {/* Completed rounds */}
          {completedRounds.length > 0 && (
            <>
              {completedRounds.map((round, idx) => (
                <RoundItem
                  key={round.id}
                  round={round}
                  index={idx}
                  matchPlayers={matchPlayers}
                  isOwner={isOwner}
                  hasActiveRound={liveRounds.length > 0}
                  onStartRound={onStartRound}
                  onViewRound={onViewRound}
                  showResumeConfirm={showResumeConfirm === round.id}
                  onResumeRound={onResumeRound}
                  onResumeConfirm={onResumeConfirm}
                  onResumeCancel={onResumeCancel}
                  isResuming={isResuming}
                  matchCompleted={matchStatus === 'completed'}
                />
              ))}
            </>
          )}
        </div>
      )}

    </div>
  )
}

const GAME_TYPE_LABELS = {
  top2: 'Top 2',
  duels: 'Duels',
  hero: 'Hero',
  fourball: '4-Ball',
  next_ball: 'Next Ball',
}

export function RoundItem({ round, matchPlayers, isOwner, hasActiveRound, onStartRound, onEndRound, onViewRound, showResumeConfirm, onResumeRound, onResumeConfirm, onResumeCancel, isResuming, matchCompleted }) {
  const gameTypeLabel = GAME_TYPE_LABELS[round.game_type] || (round.game_type !== 'stroke' ? round.game_type : null)

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: '0.75rem',
      backgroundColor: round.status === 'in_progress'
        ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.05)'
        : 'var(--bg-secondary)',
      borderRadius: '0.75rem',
      border: round.status === 'in_progress'
        ? '2px solid var(--accent-primary)'
        : '1px solid var(--border-color)',
      boxShadow: 'var(--card-shadow, none)',
      textAlign: 'left',
      width: '100%',
      minHeight: '100px',
    }}>
      <button
        onClick={() => round.status === 'in_progress' ? onStartRound() : onViewRound(round.id)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          textAlign: 'left',
          width: '100%',
        }}
      >
        {/* Top row: status badge + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            fontSize: '0.625rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '0.125rem 0.375rem',
            borderRadius: '9999px',
            ...(round.status === 'in_progress' ? {
              color: 'var(--accent-primary)',
              backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
            } : round.status === 'completed' ? {
              color: 'var(--text-muted)',
              backgroundColor: 'var(--bg-secondary)',
            } : {
              color: 'var(--accent-secondary, #f59e0b)',
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
            }),
          }}>
            {round.status === 'in_progress' ? 'Live' : round.status === 'completed' ? 'Completed' : 'Setup'}
          </span>
          <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
        </div>

        {/* Middle: icon + round name + game type badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: round.status === 'in_progress'
              ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)'
              : round.status === 'completed'
              ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)'
              : 'var(--bg-secondary)',
            flexShrink: 0,
          }}>
            {round.status === 'in_progress' ? (
              <Play size={16} style={{ color: 'var(--accent-primary)' }} />
            ) : round.status === 'completed' ? (
              <Check size={16} style={{ color: 'var(--accent-primary)' }} />
            ) : (
              <Clock size={16} style={{ color: 'var(--text-muted)' }} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                {round.game_name || round.course_name || 'Round'}
              </span>
              {gameTypeLabel && (
                <GameTypeBadge gameTypeId={round.game_type} size="small" />
              )}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
              {round.course_name && <span>{round.course_name} &middot; </span>}
              {round.num_holes} holes
              {round.wager > 0 && <span> &middot; ${Number(round.wager)}/game</span>}
            </div>
          </div>
        </div>

        {/* Bottom: winner display for completed rounds */}
        {round.status === 'completed' && (
          <div>
            <div style={{
              fontSize: '0.6875rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-muted)',
              marginBottom: '0.25rem',
            }}>
              Round Winner
            </div>
            <RoundWinnerLabel round={round} matchPlayers={matchPlayers} />
          </div>
        )}
      </button>

      {/* End Round button for live rounds (owner only) */}
      {isOwner && round.status === 'in_progress' && onEndRound && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onEndRound()
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            width: '100%',
            padding: '0.5rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--accent-primary)',
            backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
            border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.2)',
          }}
        >
          <Trophy size={14} />
          End Round
        </button>
      )}

      {/* Resume button for completed rounds (owner only, hidden when match is completed) */}
      {isOwner && !matchCompleted && round.status === 'completed' && !hasActiveRound && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onResumeRound(round.id)
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              width: '100%',
              padding: '0.5rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'var(--accent-secondary, #f59e0b)',
              backgroundColor: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
            }}
          >
            <RotateCcw size={14} />
            Resume Round
          </button>

          {/* Resume Round confirmation modal */}
          {showResumeConfirm && (
            <div
              onClick={() => onResumeCancel()}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '1rem',
                  border: '1px solid var(--border-color)',
                  padding: '1.5rem',
                  maxWidth: '340px',
                  width: '100%',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                }}
              >
                <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
                  Resume Round?
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
                  Settlements will be removed and scores can be edited again.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => onResumeConfirm(round.id)}
                    disabled={isResuming}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      fontWeight: '600',
                      borderRadius: '9999px',
                      color: '#fff',
                      backgroundColor: 'var(--accent-primary)',
                      border: 'none',
                      cursor: isResuming ? 'wait' : 'pointer',
                      opacity: isResuming ? 0.7 : 1,
                    }}
                  >
                    {isResuming ? 'Resuming...' : 'Confirm Resume Round'}
                  </button>
                  <button
                    onClick={() => onResumeCancel()}
                    style={{
                      height: '44px',
                      width: '100%',
                      fontSize: '0.9375rem',
                      borderRadius: '9999px',
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function RoundWinnerLabel({ round, matchPlayers }) {
  const { results, teamStructure } = useRoundResults(round, matchPlayers)

  if (!results || teamStructure.length < 2) return null

  let winnerText = null
  let winnerTeamIndex = null

  if (results.standings && results.standings.length >= 2) {
    const sorted = [...results.standings].sort((a, b) => b.points - a.points)
    if (sorted[0].points > sorted[1].points) {
      winnerText = sorted[0].teamName
      winnerTeamIndex = sorted[0].teamIndex
    } else {
      winnerText = 'Tied'
    }
  }

  if (!winnerText) return null

  const gamePlayers = round?.game_players || []
  const winnerPlayerIds = winnerTeamIndex != null ? teamStructure[winnerTeamIndex]?.playerIds || [] : []
  const winnerPlayers = winnerPlayerIds.map((pid) => {
    const gp = gamePlayers.find((g) => g.player_id === pid)
    return gp?.player || null
  }).filter(Boolean)

  const isTied = winnerText === 'Tied'

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '0.375rem',
      border: isTied ? '1px solid var(--border-color)' : '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.25)',
      boxShadow: 'var(--card-shadow, none)',
    }}>
      <Trophy size={16} style={{ color: isTied ? 'var(--text-muted)' : 'var(--accent-primary)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.75rem',
          fontWeight: '700',
          color: isTied ? 'var(--text-muted)' : 'var(--accent-primary)',
        }}>
          {winnerText}
        </div>
        {winnerPlayers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
            {winnerPlayers.map((player, idx) => (
              <span key={player.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                {idx > 0 && (
                  <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>&amp;</span>
                )}
                <span style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  borderRadius: '50%',
                  backgroundColor: player.avatar_color,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: getInitials(player).length > 1 ? '0.375rem' : '0.5rem',
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {getInitials(player)}
                </span>
                <span style={{ fontSize: '0.6875rem', fontWeight: '500', color: 'var(--text-secondary)' }}>
                  {player.display_name?.split(' ')[0]}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
